import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Observer } from "gsap/Observer";
import { 
  Megaphone, 
  Eye, 
  TrendingUp, 
  PlayCircle, 
  Navigation, 
  Sparkles, 
  Zap, 
  Tv2, 
  Sliders, 
  Clapperboard, 
  Radar, 
  Smartphone, 
  BarChart3, 
  Rocket,
  ArrowRight,
  ArrowUpRight,
  Check,
  CheckCircle2
} from "lucide-react";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, Observer);
}

// --- 1. PROGRAMMATIC TARGETING WAVE ---
export function ProgrammaticTargeting() {
  const containerRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const dotRef = useRef<SVGCircleElement>(null);
  
  const [reach, setReach] = useState("0M");
  const [roi, setRoi] = useState("0x");

  useEffect(() => {
    if (!containerRef.current || !pathRef.current || !dotRef.current) return;

    const path = pathRef.current;
    const dot = dotRef.current;
    const pathLength = path.getTotalLength();

    // Set initial dasharray and offset
    gsap.set(path, { strokeDasharray: pathLength, strokeDashoffset: pathLength });

    // Draw path on scroll
    const pathAnim = gsap.to(path, {
      strokeDashoffset: 0,
      ease: "none",
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top 80%",
        end: "bottom 50%",
        scrub: true,
      }
    });

    // Animate target dot along path
    const animObj = { progress: 0 };
    const pStart = path.getPointAtLength(0);
    gsap.set(dot, { cx: pStart.x, cy: pStart.y });

    const dotAnim = gsap.to(animObj, {
      progress: 1,
      ease: "none",
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top 80%",
        end: "bottom 50%",
        scrub: true,
        onUpdate: () => {
          const pt = path.getPointAtLength(animObj.progress * pathLength);
          gsap.set(dot, { cx: pt.x, cy: pt.y });
        }
      }
    });

    // Counter animations
    const reachCounter = gsap.to({ val: 0 }, {
      val: 24.5,
      duration: 2,
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top 70%",
        toggleActions: "play none none none"
      },
      onUpdate: function() {
        setReach(this.targets()[0].val.toFixed(1) + "M");
      }
    });

    const roiCounter = gsap.to({ val: 0 }, {
      val: 3.2,
      duration: 1.8,
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top 70%",
        toggleActions: "play none none none"
      },
      onUpdate: function() {
        setRoi(this.targets()[0].val.toFixed(1) + "x");
      }
    });

    return () => {
      pathAnim.kill();
      dotAnim.kill();
      reachCounter.kill();
      roiCounter.kill();
    };
  }, []);

  return (
    <section 
      ref={containerRef} 
      id="targeting" 
      className="relative py-24 md:py-36 bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="container-x grid md:grid-cols-12 gap-12 items-center">
        {/* Text Col */}
        <div className="md:col-span-5 relative z-10">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] text-muted-foreground bg-white/5 border border-white/5 mb-6">
            <Radar className="h-3.5 w-3.5 text-[var(--glow-cyan)] animate-pulse" />
            Precision Auditing
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-none text-white">
            Dynamic Target <br />
            <span className="text-gradient">Navigation.</span>
          </h2>
          <p className="mt-6 text-sm md:text-base text-muted-foreground leading-relaxed">
            Watch our algorithmic targeting engine track and capture high-intent users in real-time. As you scroll, the campaign trajectory adapts, routing budget away from waste and locking directly onto active conversion hubs.
          </p>

          <div className="mt-10 flex flex-wrap gap-8 items-center">
            <div>
              <div className="font-display text-4xl md:text-5xl font-bold text-gradient-cyan">{reach}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">Audience Reach</div>
            </div>
            <div>
              <div className="font-display text-4xl md:text-5xl font-bold text-gradient-cyan">{roi}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">Direct ROI Scale</div>
            </div>
          </div>
        </div>

        {/* Visual Col */}
        <div className="md:col-span-7 flex justify-center">
          <div className="relative w-full max-w-2xl aspect-[2/1] rounded-3xl bg-[#08080d] border border-white/10 p-6 shadow-2xl overflow-hidden flex items-center justify-center">
            {/* Visual background meshes */}
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-[var(--glow-cyan)] blur-[60px] opacity-10 pointer-events-none" />

            <svg viewBox="0 0 400 200" className="w-full h-full relative z-10" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Background guide path */}
              <path 
                d="M10,180 C80,20 120,180 200,100 C280,20 320,180 390,20" 
                stroke="rgba(255,255,255,0.05)" 
                strokeWidth="3" 
                strokeLinecap="round"
              />
              {/* Animated drawn path */}
              <path 
                ref={pathRef}
                d="M10,180 C80,20 120,180 200,100 C280,20 320,180 390,20" 
                stroke="url(#cyan-glow-grad)" 
                strokeWidth="4" 
                strokeLinecap="round"
              />
              
              {/* Target Hub Rings at (200, 100) */}
              <circle cx="200" cy="100" r="16" stroke="var(--glow-cyan)" strokeWidth="1" strokeDasharray="3 3" className="animate-spin-slow origin-center" />
              <circle cx="200" cy="100" r="8" fill="var(--glow-cyan)" opacity="0.25" />
              <circle cx="200" cy="100" r="4" fill="var(--glow-cyan)" />
              
              {/* Active tracking target dot */}
              <circle 
                ref={dotRef}
                cx="0" 
                cy="0" 
                r="6" 
                fill="#ffffff"
                stroke="var(--glow-cyan)"
                strokeWidth="2.5"
                style={{ filter: "drop-shadow(0 0 8px var(--glow-cyan))" }}
              />

              <defs>
                <linearGradient id="cyan-glow-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="var(--glow-cyan)" />
                  <stop offset="100%" stopColor="var(--glow-orange)" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- 2. OMNICHANNEL DEVICE RESIZER ---
export function DeviceSimulator() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mockupRef = useRef<HTMLDivElement>(null);
  const [deviceWidth, setDeviceWidth] = useState(1000);
  const [deviceType, setDeviceType] = useState("DESKTOP CANVAS");

  useEffect(() => {
    const handleUpdateWidths = () => {
      if (!containerRef.current || !mockupRef.current) return;
      const containerWidth = containerRef.current.offsetWidth || window.innerWidth;
      const padding = window.innerWidth < 768 ? 24 : 48;
      const startWidth = Math.min(1000, containerWidth - padding);
      const midWidth = Math.min(640, containerWidth - padding);
      const minWidth = Math.min(320, containerWidth - padding);

      gsap.set(mockupRef.current, { width: startWidth });

      const deviceTL = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "+=1200",
          pin: true,
          scrub: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            if (!mockupRef.current) return;
            const currentW = Math.round(mockupRef.current.offsetWidth);
            setDeviceWidth(currentW);
            if (currentW > 700) {
              setDeviceType("DESKTOP SCREEN");
            } else if (currentW > 450) {
              setDeviceType("TABLET VIEWPORT");
            } else {
              setDeviceType("MOBILE FEED AD");
            }
          }
        }
      });

      deviceTL.to(mockupRef.current, {
        width: midWidth,
        ease: "power2.inOut",
      }).to(mockupRef.current, {
        width: minWidth,
        ease: "power2.inOut",
      });

      return deviceTL;
    };

    const deviceTL = handleUpdateWidths();

    return () => {
      deviceTL?.kill();
    };
  }, []);

  return (
    <section 
      ref={containerRef} 
      id="responsive" 
      className="relative h-screen flex flex-col justify-start bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      <div className="container-x flex flex-col items-center justify-start pt-24 sm:pt-28 md:pt-32 pb-4 sm:pb-6 h-full max-h-screen gap-3 sm:gap-4 md:gap-5 relative z-10">
        <div className="max-w-2xl text-center relative z-10 shrink-0">
          <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full px-3.5 py-1 text-xs uppercase tracking-[0.15em] text-muted-foreground bg-white/5 border border-white/10 mb-2 sm:mb-2.5">
            <Smartphone className="h-3.5 w-3.5 text-[var(--glow-orange)] animate-pulse" />
            <span>Dynamic Canvas</span>
            <span className="text-white/20 select-none">|</span>
            <span className="font-mono text-white font-bold">{deviceWidth}px</span>
            <span className="text-white/20 select-none">|</span>
            <span className="text-[var(--glow-orange)] font-bold">{deviceType}</span>
          </div>
          <h2 className="font-display text-xl sm:text-2xl md:text-4xl font-semibold tracking-tight text-white leading-tight">
            Responsive Omnichannel <span className="text-gradient">Canvas.</span>
          </h2>
          <p className="mt-2 text-xs md:text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">
            Your advertising layout morphs dynamically. Scroll to see the same high-end media asset re-shape perfectly across Desktop, Tablet, and Mobile viewport configurations.
          </p>
        </div>

        {/* Device mockup boundary */}
        <div className="relative w-full flex-1 flex flex-col items-center justify-center min-h-0 pb-2">
          <div 
            ref={mockupRef}
            className="w-full max-w-[1000px] rounded-2xl sm:rounded-3xl bg-[#0b0b10] border border-white/10 shadow-2xl flex flex-col overflow-hidden will-change-[width] relative shrink-0 transition-[height] duration-300"
            style={{ height: "clamp(200px, 30vh, 320px)" }}
          >
            {/* Mock browser header */}
            <div className="flex items-center gap-1.5 px-5 py-3 border-b border-white/5 bg-[#07070b] shrink-0">
              <div className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <div className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <div className="h-2.5 w-2.5 rounded-full bg-white/15" />
            </div>

            {/* Custom High-Fidelity CSS Mockup Content */}
            <div className="flex-1 p-4 md:p-6 flex flex-col justify-between relative overflow-hidden select-none">
              {/* Background accent shapes */}
              <div className="absolute top-1/4 left-1/4 h-[200px] w-[200px] rounded-full bg-[var(--glow-purple)] blur-[80px] opacity-15 pointer-events-none" />
              
              <div className="relative z-10 w-full flex flex-col justify-between h-full">
                {/* Simulated Ad Header */}
                <div className="flex items-center gap-3 shrink-0">
                  <div className="h-9 w-9 rounded-xl bg-[var(--gradient-primary)] flex items-center justify-center font-display font-bold text-xs text-white">
                    K
                  </div>
                  <div>
                    <div className="text-xs font-semibold flex items-center gap-1 text-white">
                      Kris Media Work
                      <Check className="h-3 w-3 bg-[var(--glow-cyan)] text-black rounded-full p-0.5" />
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground/60 mt-0.5">Sponsored</div>
                  </div>
                </div>

                {/* Simulated Campaign Content */}
                <div className="my-auto max-w-sm py-1 md:py-2">
                  <h4 className="font-display text-base md:text-xl font-semibold leading-tight text-white">
                    We Scale <span className="text-gradient">Digital Feeds</span> Into ROI Channels
                  </h4>
                  <p className="mt-2 text-[10px] md:text-[11px] text-muted-foreground leading-relaxed">
                    Custom-designed interactive platforms and programmatic media buying matrices engineered to lock directly onto consumer intent.
                  </p>
                </div>

                {/* Simulated Action Footer */}
                <div className="flex items-center justify-between border-t border-white/5 pt-3 md:pt-4 shrink-0">
                  <div>
                    <div className="text-[9px] font-mono text-muted-foreground/60">CONVERSION METRIC</div>
                    <div className="text-xs font-semibold text-[var(--glow-orange)]">+312% Growth</div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold bg-white/5 border border-white/10 text-white">
                    Learn More <ArrowRight className="h-3 w-3" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- 3. PROCESS TURBINE (STAGE TIMELINE) ---
export function MediaTurbine() {
  const containerRef = useRef<HTMLDivElement>(null);
  const visualColRef = useRef<HTMLDivElement>(null);
  
  const [activeStep, setActiveStep] = useState(1);

  useEffect(() => {
    if (!containerRef.current || !visualColRef.current) return;

    const visualCol = visualColRef.current;
    const isMobile = window.innerWidth < 768;

    // Pin right side visual, let scrolling panels on left flow past (only on desktop)
    let pinTrigger: any;
    if (!isMobile) {
      pinTrigger = ScrollTrigger.create({
        trigger: containerRef.current,
        start: "top top",
        end: "bottom bottom",
        pin: visualCol,
        scrub: true,
        pinSpacing: false
      });
    }

    // Rotate rotor group on scroll (not the whole SVG)
    const rotAnim1 = gsap.to("#turbine-rotor", {
      rotation: 540,
      transformOrigin: "100px 100px",
      ease: "none",
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: "bottom bottom",
        scrub: true
      }
    });

    const rotAnim2 = gsap.to(["#blade-1", "#blade-2", "#blade-3", "#blade-4"], {
      rotation: 90,
      scale: 0.9,
      transformOrigin: "100px 100px",
      ease: "none",
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: "bottom bottom",
        scrub: true
      }
    });

    // Stage activation hooks
    const panels = ["panel-1", "panel-2", "panel-3", "panel-4"];
    const triggers = panels.map((pId, idx) => {
      return ScrollTrigger.create({
        trigger: `#${pId}`,
        start: "top center",
        end: "bottom center",
        onEnter: () => setActiveStep(idx + 1),
        onEnterBack: () => setActiveStep(idx + 1)
      });
    });

    return () => {
      if (pinTrigger) pinTrigger.kill();
      rotAnim1.kill();
      rotAnim2.kill();
      triggers.forEach(t => t.kill());
    };
  }, []);

  return (
    <section 
      ref={containerRef} 
      id="process" 
      className="relative z-10 bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="container-x grid md:grid-cols-2 gap-12 relative">
        
        {/* Left Column scrolling past */}
        <div className="py-12 md:py-36 space-y-16 md:space-y-36">
          <div className="scroll-panel flex flex-col justify-center min-h-[40vh] md:min-h-[50vh]" id="panel-1">
            <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-white">
              Demographic Auditing & Audience Diagnostics
            </h3>
            <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed max-w-md">
              We deploy advanced mathematical parsing models to isolate consumer browsing footprints, geo-behavior, and medium interactions before drawing up initial ad allocations.
            </p>
          </div>

          <div className="scroll-panel flex flex-col justify-center min-h-[40vh] md:min-h-[50vh]" id="panel-2">
            <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-white">
              High-Fidelity Canvas Creative Assembly
            </h3>
            <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed max-w-md">
              Our design studio processes high-end cinematic assets, vertical social feed micro-content, and bright outdoor billboard vector layouts engineered for exact target canvas specifications.
            </p>
          </div>

          <div className="scroll-panel flex flex-col justify-center min-h-[40vh] md:min-h-[50vh]" id="panel-3">
            <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-white">
              Algorithmic Auction Routing
            </h3>
            <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed max-w-md">
              KMWA bidding algorithms plug directly into premier real-time auction exchanges, purchasing visibility at the lowest rates when consumer interest signals hit target patterns.
            </p>
          </div>

          <div className="scroll-panel flex flex-col justify-center min-h-[40vh] md:min-h-[50vh]" id="panel-4">
            <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-white">
              Continuous Loop Data Optimizations
            </h3>
            <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed max-w-md">
              We audit every dollar spent with transparent tracking and post-click diagnostics, applying continuous optimization passes to eliminate budget waste and scale returns.
            </p>
          </div>
        </div>

        {/* Right Column Pinned visual wheel */}
        <div 
          ref={visualColRef}
          className="hidden md:flex h-screen flex-col items-center justify-center shrink-0 self-start"
        >
          <div className="w-full max-w-[580px] aspect-[45/20] relative select-none">
            <svg className="w-full h-full" viewBox="0 0 450 200" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="active-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="var(--glow-orange)" />
                  <stop offset="100%" stopColor="var(--glow-purple)" />
                </linearGradient>
              </defs>
              {/* 1. ROTATING ROTOR GROUP */}
              <g id="turbine-rotor">
                <circle cx="100" cy="100" r="90" stroke="rgba(255,255,255,0.02)" strokeWidth="1" />
                <circle cx="100" cy="100" r="75" stroke="rgba(255,255,255,0.08)" strokeWidth="2" strokeDasharray="6 6" />
                <circle cx="100" cy="100" r="55" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                
                {/* Glowing core base (gradient) */}
                <circle cx="100" cy="100" r="30" fill="rgba(18,20,20,0.95)" stroke="url(#active-grad)" strokeWidth="1.5" />
                
                {/* Shutter blades (gradient) */}
                <path d="M100,72 L112,100 L100,100 Z" fill="url(#active-grad)" opacity="0.85" id="blade-1" transformOrigin="100 100" />
                <path d="M128,100 L100,112 L100,100 Z" fill="url(#active-grad)" opacity="0.85" id="blade-2" transformOrigin="100 100" />
                <path d="M100,128 L88,100 L100,100 Z" fill="url(#active-grad)" opacity="0.85" id="blade-3" transformOrigin="100 100" />
                <path d="M72,100 L100,88 L100,100 Z" fill="url(#active-grad)" opacity="0.85" id="blade-4" transformOrigin="100 100" />
              </g>

              {/* 2. STATIC INDICATORS GROUP (Curved arc path alignment) */}
              <g id="turbine-indicators">
                {[
                  { text: "IMPRESSION INITIALIZED", step: 1, cx: 164.95, cy: 60.0, tx: 184.0, ty: 60.0 },
                  { text: "ASSET LOADED", step: 2, cx: 173.86, cy: 86.5, tx: 193.0, ty: 86.5 },
                  { text: "BID CAPTURED", step: 3, cx: 173.86, cy: 113.5, tx: 193.0, ty: 113.5 },
                  { text: "CONVERSION SECURED", step: 4, cx: 164.95, cy: 140.0, tx: 184.0, ty: 140.0 }
                ].map((item, index) => {
                  const isActive = activeStep === item.step;
                  return (
                    <g key={index} className="transition-all duration-500">
                      {/* Connecting guide circle/dot on the circumference */}
                      <circle
                        cx={item.cx}
                        cy={item.cy}
                        r={isActive ? "4.5" : "3"}
                        className="transition-all duration-500"
                        fill={isActive ? "url(#active-grad)" : "transparent"}
                        stroke={isActive ? "url(#active-grad)" : "rgba(255,255,255,0.4)"}
                        strokeWidth="1.8"
                        style={isActive ? { filter: "drop-shadow(0 0 6px var(--glow-purple))" } : undefined}
                      />
                      
                      {/* Connecting dotted leader line to indicator */}
                      <line 
                        x1={item.cx + 5} 
                        y1={item.cy} 
                        x2={item.tx - 8} 
                        y2={item.ty} 
                        stroke={isActive ? "var(--glow-purple)" : "rgba(255,255,255,0.15)"} 
                        strokeWidth="1" 
                        strokeDasharray={isActive ? "none" : "2 2"} 
                      />

                      {/* Text Indicator on the right */}
                      <text
                        x={item.tx}
                        y={item.ty + 4}
                        fill={isActive ? "#ffffff" : "rgba(255,255,255,0.70)"}
                        fontSize="11"
                        fontFamily="ui-monospace, monospace"
                        letterSpacing="0.08em"
                        fontWeight={isActive ? "bold" : "600"}
                        className="transition-all duration-300"
                      >
                        {item.text}
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- 4. BREEZE FLIGHT VECTOR PATH ---
export function BreezeFlight() {
  const containerRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const courierRef = useRef<HTMLDivElement>(null);

  const [activeStation, setActiveStation] = useState<number>(0);

  useEffect(() => {
    if (!containerRef.current || !pathRef.current || !courierRef.current) return;

    const path = pathRef.current;
    const courier = courierRef.current;
    const pathLength = path.getTotalLength();

    // Set starting position at Stage 1
    const pStart = path.getPointAtLength(0);
    gsap.set(courier, { x: pStart.x, y: pStart.y, rotation: 35 });

    // Strict scroll-pinned ScrollTrigger: Screen stays 100% still until Stage 4 completes!
    const st = ScrollTrigger.create({
      trigger: containerRef.current,
      start: "top top",
      end: "+=2200",
      pin: true,
      pinSpacing: true,
      scrub: 0.6,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        const rawProgress = self.progress;
        
        // Progress:
        // 0.0 -> 0.85: Plane travels along path from Stage 01 across to Stage 04
        // 0.85 -> 1.0: Plane sits firmly at the completed Stage 04 end position while screen remains strictly still and pinned
        const flightProgress = Math.min(1, Math.max(0, rawProgress / 0.85));
        
        const pCurrent = path.getPointAtLength(flightProgress * pathLength);
        
        // Calculate tangent angle for smooth courier orientation
        const pPrev = path.getPointAtLength(Math.max(0, flightProgress - 0.01) * pathLength);
        const angleRad = Math.atan2(pCurrent.y - pPrev.y, pCurrent.x - pPrev.x);
        const angleDeg = (angleRad * 180) / Math.PI;

        gsap.set(courier, {
          x: pCurrent.x,
          y: pCurrent.y,
          rotation: angleDeg + 45
        });

        // Stage activation:
        let activeIdx = 0;
        if (flightProgress >= 0.78) {
          activeIdx = 3; // Stage 04 reached and completed
        } else if (flightProgress >= 0.48) {
          activeIdx = 2; // Stage 03
        } else if (flightProgress >= 0.18) {
          activeIdx = 1; // Stage 02
        } else {
          activeIdx = 0; // Stage 01
        }

        setActiveStation(activeIdx);
      }
    });

    const timer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 250);

    return () => {
      clearTimeout(timer);
      st.kill();
    };
  }, []);

  return (
    <section 
      ref={containerRef} 
      id="breeze" 
      className="relative z-20 min-h-screen h-screen flex flex-col justify-start bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="container-x flex flex-col justify-start pt-24 sm:pt-28 md:pt-32 pb-4 sm:pb-6 h-full max-h-screen relative z-10">
        <div className="max-w-2xl mb-3 sm:mb-5 relative z-10 shrink-0">
          <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1 text-xs uppercase tracking-[0.2em] text-muted-foreground bg-white/5 border border-white/10 mb-2">
            <Zap className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
            Continuous Integration
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-5xl font-semibold tracking-tight text-white leading-tight">
            Continuous Campaign <br />
            <span className="text-gradient">Flight Vector.</span>
          </h2>
          <p className="mt-2 text-xs sm:text-sm md:text-base text-muted-foreground max-w-xl leading-relaxed">
            Scroll to launch the campaign courier. Watch it traverse our media stations, activating campaign layers sequentially from strategy drafts to live public launch.
          </p>
        </div>

        {/* Mobile View: Vertical Flight Vector Stations (md:hidden) */}
        <div className="grid md:hidden grid-cols-1 gap-3 w-full flex-1 overflow-y-auto min-h-0 pb-4">
          {[
            { title: "Strategy Mapping", desc: "Target Profiles Formulated", idx: 0, color: "var(--glow-orange)" },
            { title: "Creative Sync", desc: "Mockups Built", idx: 1, color: "var(--glow-purple)" },
            { title: "Programmatic Bidding", desc: "Auction Live", idx: 2, color: "var(--glow-cyan)" },
            { title: "Data Loop Integration", desc: "Conversion Systems Dynamic", idx: 3, color: "var(--glow-orange)" }
          ].map((st, sIdx) => {
            const isActive = activeStation >= st.idx;
            return (
              <div 
                key={sIdx}
                className="p-4 rounded-2xl border bg-[#0d0d15]/95 shadow-xl transition-all duration-500"
                style={{ 
                  borderColor: isActive ? st.color : "rgba(255,255,255,0.08)",
                  boxShadow: isActive ? `0 0 15px -4px ${st.color}` : "none",
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span 
                      className={`h-2 w-2 rounded-full ${isActive ? "animate-pulse" : ""}`}
                      style={{ backgroundColor: st.color, boxShadow: `0 0 10px ${st.color}` }} 
                    />
                    <span className="text-xs font-mono font-bold tracking-widest text-white/90">
                      STAGE 0{st.idx + 1}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-white/5 px-2.5 py-0.5 rounded border border-white/5">
                    {isActive ? "Active" : "Pending"}
                  </span>
                </div>
                <h4 className="text-sm font-bold text-white mt-1.5">{st.title}</h4>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{st.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Desktop View: Interactive Vector Path (hidden md:block) */}
        <div className="hidden md:block relative w-full flex-1 min-h-[350px] max-h-[440px] border border-white/10 bg-[#08080c] rounded-3xl p-6 md:p-8 shadow-2xl overflow-hidden select-none">
          <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
          
          <svg className="w-full h-full absolute inset-0 pointer-events-none z-10" fill="none" viewBox="0 0 1000 450" xmlns="http://www.w3.org/2000/svg">
            <path 
              ref={pathRef}
              id="flight-drawn-path" 
              d="M 100 105 C 240 105, 260 303, 385 303 C 510 303, 520 105, 645 105 C 770 105, 780 303, 960 303" 
              stroke="var(--glow-purple)" 
              strokeWidth="2.5" 
              strokeDasharray="6 6" 
              opacity="0.6"
            />
            {/* Flying courier arrow as an SVG group */}
            <g 
              ref={courierRef}
              className="will-change-transform"
            >
              {/* Circular backing container */}
              <circle cx="0" cy="0" r="18" fill="#0c0c14" stroke="var(--glow-purple)" strokeWidth="2" opacity="0.95" />
              {/* Navigation cursor pointing top-right */}
              <path 
                d="M -6 6 L 8 -8 L -3 9 L -4 2 L -6 6 Z" 
                fill="var(--glow-purple)" 
                stroke="var(--glow-purple)" 
                strokeWidth="1.2"
              />
            </g>
          </svg>

          {/* Overlaid Stations */}
          <div className="absolute top-0 bottom-0 left-0 right-0 flex pointer-events-none z-20">
            {[
              { title: "Strategy Mapping", desc: "Target Profiles Formulated", top: "10%", left: "6%", idx: 0, color: "var(--glow-orange)" },
              { title: "Creative Sync", desc: "Mockups Built", top: "54%", left: "28%", idx: 1, color: "var(--glow-purple)" },
              { title: "Programmatic Bidding", desc: "Auction Live", top: "10%", left: "54%", idx: 2, color: "var(--glow-cyan)" },
              { title: "Data Loop Integration", desc: "Conversion Systems Dynamic", top: "54%", left: "74%", idx: 3, color: "var(--glow-orange)" }
            ].map((st, sIdx) => {
              const isActive = activeStation >= st.idx;
              return (
                <div 
                  key={sIdx}
                  className={`absolute p-4 sm:p-5 rounded-2xl border transition-all duration-500 bg-[#0d0d15]/95 shadow-xl ${
                    isActive 
                      ? "opacity-100 translate-y-0 scale-100" 
                      : "opacity-40 translate-y-3 scale-95"
                  }`}
                  style={{ 
                    top: st.top, 
                    left: st.left, 
                    borderColor: isActive ? st.color : "rgba(255,255,255,0.08)",
                    boxShadow: isActive ? `0 0 20px -4px ${st.color}` : "none",
                    width: "215px"
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span 
                      className={`h-2 w-2 rounded-full transition ${
                        isActive ? "animate-pulse" : ""
                      }`}
                      style={{ backgroundColor: isActive ? st.color : "rgba(255,255,255,0.2)", boxShadow: isActive ? `0 0 8px ${st.color}` : "none" }} 
                    />
                    <span className="text-xs font-mono tracking-widest font-bold text-white/90">
                      STAGE 0{st.idx + 1}
                    </span>
                  </div>
                  <h4 className="text-sm font-bold text-white mt-1.5">{st.title}</h4>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{st.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// --- 5. TESTIMONIALS SPIRAL ---
export function TestimonialsSpiral() {
  const testimonials = [
    {
      brand: "Aether Cosmetics",
      icon: Sparkles,
      quote: "Kris Media Work scaled our direct-to-consumer store conversions by 240% inside 90 days. Exceptional programmatic and SEO support.",
      name: "Elena Rostova",
      role: "VP of Marketing",
      color: "var(--glow-purple)"
    },
    {
      brand: "Volt Motors",
      icon: Zap,
      quote: "The dynamic resizing mobile ad campaigns secured massive CTR spikes across our luxury SUV digital pre-order feeds.",
      name: "Marcus Vance",
      role: "Head of Communications",
      color: "var(--glow-cyan)"
    },
    {
      brand: "Prime Stream",
      icon: Tv2,
      quote: "Their localized digital billboard campaign in urban cores brought unprecedented viewer recall metrics for our series launch.",
      name: "Sarah Jenkins",
      role: "Global Brand Director",
      color: "var(--glow-orange)"
    }
  ];

  return (
    <section 
      id="spiral" 
      className="relative z-30 bg-black border-t border-white/5 overflow-hidden py-20 sm:py-24 md:py-28 lg:py-32"
    >
      <div className="container-x flex flex-col gap-10 sm:gap-14 relative">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 max-w-5xl">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1 text-xs uppercase tracking-[0.25em] text-muted-foreground bg-white/5 border border-white/10 mb-4">
              <Sparkles className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Client Reviews
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white leading-tight">
              What Our <span className="text-gradient">Clients Say.</span>
            </h2>
          </div>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-md">
            Verified feedback from enterprise and high-growth partners who scale their digital acquisition and media footprint with Kris Media Work.
          </p>
        </div>

        {/* 3 Full-Content Testimonial Cards in Responsive Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((t, idx) => {
            const IconComp = t.icon;
            return (
              <div 
                key={idx}
                className="rounded-3xl p-6 sm:p-8 bg-[#0a0a10] border border-white/10 shadow-xl flex flex-col justify-between hover:border-white/20 transition-all duration-300 group relative overflow-hidden"
              >
                {/* Glow accent */}
                <div 
                  className="absolute -top-16 -right-16 w-32 h-32 rounded-full opacity-10 blur-2xl pointer-events-none group-hover:opacity-20 transition-opacity"
                  style={{ backgroundColor: t.color }}
                />

                <div className="relative z-10">
                  <div className="flex items-center gap-2" style={{ color: t.color }}>
                    <IconComp className="h-4 w-4" />
                    <span className="text-xs font-mono font-bold uppercase tracking-wider">{t.brand}</span>
                  </div>
                  <p className="mt-6 text-sm sm:text-base text-white/90 leading-relaxed font-accent italic">
                    "{t.quote}"
                  </p>
                </div>

                <div className="border-t border-white/10 pt-5 mt-6 relative z-10 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-white">{t.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.role}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// --- 6. PARTICLE WAVES WEBGL STORY ---
export function ParticleWaves() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    let ww = window.innerWidth;
    let wh = window.innerHeight;

    // --- THREE.JS SETUP ---
    const renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      antialias: true,
      alpha: true, // Transparent background to blend nicely with CSS radial gradients
    });
    renderer.setSize(ww, wh);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x000000, 0.0007);

    const camera = new THREE.PerspectiveCamera(55, ww / wh, 1, 3000);
    camera.position.z = 900;
    camera.position.y = 250;

    // --- COLOR GRADIENT AND CANVAS TEXTURE FOR GLOWING PARTICLES ---
    const createCircleTexture = () => {
      const size = 64;
      const canvasTex = document.createElement("canvas");
      canvasTex.width = size;
      canvasTex.height = size;
      const ctx = canvasTex.getContext("2d");
      if (ctx) {
        // Soft glowing particle circle
        const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
        grad.addColorStop(0, "rgba(255, 255, 255, 1)");
        grad.addColorStop(0.3, "rgba(255, 255, 255, 0.8)");
        grad.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      return new THREE.CanvasTexture(canvasTex);
    };

    const circleTexture = createCircleTexture();
    const SEPARATION = 90;
    const AMOUNTX = 22;
    const AMOUNTY = 22;
    const particles: THREE.Sprite[] = [];

    // Premium theme colors: orange-pink to violet-purple
    const colorStart = new THREE.Color(0xff6a3d); // Brand Coral Orange
    const colorEnd = new THREE.Color(0xa855f7);   // Brand Violet Purple

    for (let ix = 0; ix < AMOUNTX; ix++) {
      for (let iy = 0; iy < AMOUNTY; iy++) {
        // Linearly interpolate colors across the grid diagonal
        const lerpVal = (ix / AMOUNTX + iy / AMOUNTY) / 2;
        const color = new THREE.Color().copy(colorStart).lerp(colorEnd, lerpVal);

        const material = new THREE.SpriteMaterial({
          map: circleTexture,
          color: color,
          transparent: true,
          opacity: 0.85,
        });

        const particle = new THREE.Sprite(material);
        particle.position.x = ix * SEPARATION - ((AMOUNTX * SEPARATION) / 2);
        particle.position.z = iy * SEPARATION - ((AMOUNTY * SEPARATION) / 2);
        particle.position.y = 0;
        particle.scale.setScalar(8);

        scene.add(particle);
        particles.push(particle);
      }
    }

    // --- SCROLL ANIMATION AND CAMERA PARALLAX (GSAP ScrollTrigger) ---
    const waveTL = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: "+=1500",
        pin: true,
        scrub: true,
        onUpdate: (self) => {
          // Camera flies forward and sweeps slightly on scroll
          camera.position.z = 900 - self.progress * 1300;
          camera.position.y = 220 + Math.sin(self.progress * Math.PI) * 120;
          camera.lookAt(0, -60, 0);
        }
      }
    });

    // Set initial states explicitly to prevent layout jump or flash of unstyled content
    gsap.set("#wave-text-1", { autoAlpha: 1, y: 0 });
    gsap.set(["#wave-text-2", "#wave-text-3"], { autoAlpha: 0, y: 30 });

    // Animate opacity and movement of text overlay slides sequentially
    // Duration ratios: 
    // 0.0 - 0.4: Slide 1 visible
    // 0.4 - 1.2: Slide 1 fades out to -30
    // 1.2 - 2.0: Slide 2 fades in from +30 to 0
    // 2.0 - 2.4: Slide 2 visible
    // 2.4 - 3.2: Slide 2 fades out to -30
    // 3.2 - 4.0: Slide 3 fades in from +30 to 0
    waveTL
      .to("#wave-text-1", { autoAlpha: 0, y: -30, duration: 0.8, ease: "power1.inOut" }, 0.4)
      .to("#wave-text-2", { autoAlpha: 1, y: 0, duration: 0.8, ease: "power1.inOut" }, 1.2)
      .to("#wave-text-2", { autoAlpha: 0, y: -30, duration: 0.8, ease: "power1.inOut" }, 2.4)
      .to("#wave-text-3", { autoAlpha: 1, y: 0, duration: 0.8, ease: "power1.inOut" }, 3.2)
      .to({}, { duration: 0.8 }); // Stretches the timeline scroll zone for slide 3 to be read

    // --- ANIMATION FRAME LOOP (PAUSED WHEN OFF-SCREEN) ---
    let count = 0;
    let animationActive = false;
    let animationFrameId: number;

    const viewTrigger = ScrollTrigger.create({
      trigger: containerRef.current,
      start: "top bottom",
      end: "bottom top",
      onToggle: (self) => {
        animationActive = self.isActive;
      }
    });

    const render = () => {
      let i = 0;
      for (let ix = 0; ix < AMOUNTX; ix++) {
        for (let iy = 0; iy < AMOUNTY; iy++) {
          const particle = particles[i++];
          if (!particle) continue;

          // Double sine wave function to create flowing ripples
          particle.position.y = (Math.sin((ix + count) * 0.3) * 45) + (Math.sin((iy + count) * 0.5) * 45);
          
          // Ripple the scale size along with height
          const scaleVal = ((Math.sin((ix + count) * 0.3) + 1) * 3) + ((Math.sin((iy + count) * 0.5) + 1) * 3);
          particle.scale.setScalar(Math.max(4, scaleVal));
        }
      }

      renderer.render(scene, camera);
      count += 0.04;
    };

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      if (animationActive) {
        render();
      }
    };

    animate();

    // --- WINDOW RESIZE HANDLER ---
    const handleResize = () => {
      ww = window.innerWidth;
      wh = window.innerHeight;
      camera.aspect = ww / wh;
      camera.updateProjectionMatrix();
      renderer.setSize(ww, wh);
    };
    window.addEventListener("resize", handleResize);

    // --- LIFECYCLE CLEANUP ---
    return () => {
      waveTL.kill();
      viewTrigger.kill();
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      
      particles.forEach((p) => {
        p.material.dispose();
      });
      circleTexture.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <section 
      ref={containerRef} 
      id="waves"
      className="relative h-screen bg-black overflow-hidden flex items-center justify-center border-t border-white/5"
    >
      {/* Background accents & subtle grid pattern */}
      <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none z-0" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[var(--glow-purple)] blur-[140px] opacity-10 pointer-events-none z-0" />

      {/* WebGL Particle Waves Canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />

      {/* Text Storytelling Slides Overlay */}
      <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
        <div className="container-x text-center max-w-3xl relative w-full h-full flex items-center justify-center">
          
          {/* Slide 1 */}
          <div id="wave-text-1" className="absolute px-6 w-full" style={{ opacity: 1, visibility: "visible" }}>
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] text-muted-foreground bg-white/5 border border-white/5 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--glow-orange)] shadow-[0_0_10px_var(--glow-orange)]" />
              Campaign Flight Dynamics
            </div>
            <h3 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-none">
              Isolating Consumer <br />
              <span className="text-gradient">Attention Footprints.</span>
            </h3>
            <p className="mt-4 text-xs md:text-sm text-muted-foreground max-w-lg mx-auto">
              Our mathematical models parse digital browsing footprints to predict user trajectory before ad spend triggers.
            </p>
          </div>

          {/* Slide 2 */}
          <div id="wave-text-2" className="absolute px-6 w-full" style={{ opacity: 0, visibility: "hidden" }}>
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] text-muted-foreground bg-white/5 border border-white/5 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--glow-purple)] shadow-[0_0_10px_var(--glow-purple)]" />
              Real-Time Optimization
            </div>
            <h3 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-none">
              Synthesizing High-Intent <br />
              <span className="text-gradient">Conversion Streams.</span>
            </h3>
            <p className="mt-4 text-xs md:text-sm text-muted-foreground max-w-lg mx-auto">
              We dynamically route programmatic capital away from waste, funnelling budget directly into active conversion hubs.
            </p>
          </div>

          {/* Slide 3 */}
          <div id="wave-text-3" className="absolute px-6 w-full" style={{ opacity: 0, visibility: "hidden" }}>
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] text-muted-foreground bg-white/5 border border-white/5 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--glow-cyan)] shadow-[0_0_10px_var(--glow-cyan)]" />
              Sustainable Compounding
            </div>
            <h3 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-none">
              Securing Exponential <br />
              <span className="text-gradient">ROI Channel Scale.</span>
            </h3>
            <p className="mt-4 text-xs md:text-sm text-muted-foreground max-w-lg mx-auto">
              We audit every dollar with transparent attribution systems, building growth funnels that compound month-over-month.
            </p>
          </div>

        </div>
      </div>
    </section>
  );
}

// --- 7. SCROLL PHYSICS SHOWCASE ---
export function ScrollPhysicsShowcase() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Select all elements with data-speed attributes
    const elements = containerRef.current.querySelectorAll("[data-speed]");
    const triggers: ScrollTrigger[] = [];

    elements.forEach((el) => {
      const speedStr = el.getAttribute("data-speed");
      
      if (speedStr === "auto") {
        // Auto parallax image effect (slides inside overflow-hidden parent)
        const img = el as HTMLImageElement;
        const parent = img.parentElement;
        if (parent) {
          const anim = gsap.fromTo(img,
            { yPercent: -15 },
            {
              yPercent: 15,
              ease: "none",
              scrollTrigger: {
                trigger: parent,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
              }
            }
          );
          triggers.push(anim.scrollTrigger!);
        }
      } else {
        // Vertical speed shifting relative to scroll flow
        const speed = parseFloat(speedStr || "1");
        if (speed !== 1) {
          const shift = (speed - 1) * 160; // Scale factor for speed translation
          const anim = gsap.fromTo(el,
            { y: shift },
            {
              y: -shift,
              ease: "none",
              scrollTrigger: {
                trigger: el.parentElement || el,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
              }
            }
          );
          triggers.push(anim.scrollTrigger!);
        }
      }
    });

    // Staggered letter lag lag-chars
    const chars = containerRef.current.querySelectorAll(".lag-char");
    chars.forEach((char, i) => {
      const anim = gsap.fromTo(char,
        { y: 25 * (i + 1) * 0.15 },
        {
          y: -25 * (i + 1) * 0.15,
          ease: "none",
          scrollTrigger: {
            trigger: char.parentElement,
            start: "top bottom",
            end: "bottom top",
            scrub: 1 + i * 0.08, // Adding incremental scrub delay creates the fluid stagger lag
          }
        }
      );
      triggers.push(anim.scrollTrigger!);
    });

    return () => {
      triggers.forEach((t) => t.kill());
    };
  }, []);

  return (
    <section 
      ref={containerRef} 
      id="physics"
      className="relative bg-black border-t border-white/5 py-12 md:py-24 overflow-hidden"
    >
      {/* Background accents */}
      <div className="absolute top-0 bottom-0 left-0 right-0 grid-bg opacity-5 pointer-events-none z-0" />

      {/* 1. Header (smooooth scrolling) */}
      <div className="relative min-h-[50vh] flex flex-col items-center justify-center text-center overflow-hidden">
        <div className="heading opacity-100 flex flex-col items-center justify-center relative z-10 select-none">
          <span className="text-xs font-mono tracking-[0.3em] uppercase text-[var(--glow-purple)]">Physics Engine</span>
          <h2 className="font-display font-black text-6xl md:text-[8vw] uppercase leading-none tracking-tight text-white mt-4">
            SMOOOOTH
          </h2>
          <div className="relative text-container h-[45px] md:h-[110px] w-full flex items-center justify-center mt-2">
            {[0.95, 0.9, 0.85, 0.8, 0.75, 0.7].map((speed, idx) => (
              <p 
                key={idx} 
                data-speed={speed} 
                className="absolute font-display font-black text-5xl md:text-[6vw] uppercase leading-none tracking-widest pointer-events-none select-none text-transparent"
                style={{
                  WebkitTextStrokeWidth: "1px",
                  WebkitTextStrokeColor: `rgba(168, 85, 247, ${0.9 - idx * 0.15})`,
                  top: `${idx * 6}px`
                }}
              >
                SCROLLING
              </p>
            ))}
            <p className="absolute font-display font-black text-5xl md:text-[6vw] uppercase leading-none tracking-widest text-white top-0">
              SCROLLING
            </p>
          </div>
        </div>
      </div>

      {/* 2. Image Grid */}
      <div className="image-grid container-x py-12 md:py-24 grid grid-cols-3 gap-4 md:gap-8 max-w-5xl mx-auto z-10 relative">
        <div className="image_cont aspect-square overflow-hidden rounded-3xl border border-white/5 bg-white/5 relative" data-speed="1.1">
          <img data-speed="auto" src="/images/showcase/story-1.jpg" className="w-full h-[140%] object-cover absolute top-0" alt="KMWA Visual Showcase 1" />
        </div>
        <div className="image_cont aspect-square overflow-hidden rounded-3xl border border-white/5 bg-white/5 relative mt-16 md:mt-24" data-speed="1.3">
          <img data-speed="auto" src="/images/showcase/story-2.jpg" className="w-full h-[140%] object-cover absolute top-0" alt="KMWA Visual Showcase 2" />
        </div>
        <div className="image_cont aspect-square overflow-hidden rounded-3xl border border-white/5 bg-white/5 relative mt-32 md:mt-48" data-speed="1.2">
          <img data-speed="auto" src="/images/showcase/story-3.jpg" className="w-full h-[140%] object-cover absolute top-0" alt="KMWA Visual Showcase 3" />
        </div>
      </div>

      {/* 3. Title info */}
      <div className="title container-x py-16 md:py-24 text-center max-w-2xl mx-auto relative z-10">
        <h3 className="font-display text-xs font-mono uppercase tracking-[0.3em] text-[var(--glow-orange)]">Interactive Mechanics</h3>
        <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold tracking-tight text-white leading-tight">
          Scroll Physics <br />
          <span className="text-gradient">Inertia Engine.</span>
        </h2>
        <p className="mt-6 text-sm md:text-base text-muted-foreground max-w-lg mx-auto leading-relaxed">
          Integrated directly with the viewport using hardware-accelerated vectors. Animates layouts along scrolling bounds, using speed offsets and staggered lags.
        </p>
      </div>

      {/* 4. Speed Control Bars */}
      <div className="container-x py-16 md:py-24 max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10">
        <div className="bars-text pr-6">
          <div className="content border-l-2 border-[var(--glow-purple)] pl-6">
            <span className="text-xs font-mono tracking-[0.2em] uppercase text-muted-foreground">Deceleration & Acceleration</span>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white mt-2">Speed Control.</h3>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Animate elements along the viewport at different speeds — slow them down or make them whizz past to create high-fidelity 3D depth layers on scroll.
            </p>
          </div>
        </div>

        <div className="bars-cont flex items-end justify-between h-[280px] md:h-[360px] bg-white/[0.02] border border-white/5 rounded-3xl p-6 md:p-10 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
          {[
            { speed: "0.85", label: "0.85", bg: "from-[var(--glow-orange)]/10 to-[var(--glow-orange)]" },
            { speed: "0.95", label: "0.95", bg: "from-[var(--glow-purple)]/10 to-[var(--glow-purple)]" },
            { speed: "1.0", label: "1.0", bg: "from-[var(--glow-cyan)]/10 to-[var(--glow-cyan)]" },
            { speed: "1.1", label: "1.1", bg: "from-[var(--glow-orange)]/10 to-[var(--glow-orange)]" },
            { speed: "1.2", label: "1.2", bg: "from-[var(--glow-purple)]/10 to-[var(--glow-purple)]" }
          ].map((bar, idx) => (
            <div 
              key={idx} 
              data-speed={bar.speed} 
              className={`w-[12%] md:w-[15%] rounded-2xl bg-gradient-to-t ${bar.bg} flex items-center justify-center py-6 shadow-2xl relative select-none`}
              style={{ height: `${40 + idx * 12}%` }}
            >
              <span className="font-mono font-bold text-[10px] md:text-sm text-black rotate-270 uppercase tracking-widest">{bar.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 5. Parallax Viewport Slab */}
      <div className="py-16 md:py-24 overflow-hidden relative z-10">
        <div className="parallax-slab h-[280px] md:h-[400px] w-full overflow-hidden border-y border-white/5 relative">
          <img 
            data-speed="auto" 
            src="/images/showcase/banner-wide.jpg" 
            className="absolute bottom-0 w-full h-[160%] object-cover" 
            alt="Parallax Showcase Landscape" 
          />
          <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
            <span className="font-display font-bold text-base md:text-2xl text-white uppercase tracking-[0.25em] bg-black/75 backdrop-blur-md px-6 py-3 border border-white/10 rounded-full select-none">
              PARALLAX IMAGE SLAB
            </span>
          </div>
        </div>
      </div>

      {/* 6. Stagger letter lag */}
      <div className="container-x py-16 md:py-24 max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10">
        <div className="staggered_text">
          <div className="content border-l-2 border-[var(--glow-orange)] pl-6">
            <span className="text-xs font-mono tracking-[0.2em] uppercase text-muted-foreground">Follow Through Action</span>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white mt-2">Add Some Lag.</h3>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Loosen the scroll anchor on specific sub-elements to create a responsive lag effect. This simulates dynamic inertia and follow-through properties.
            </p>
          </div>
        </div>

        <div className="staggered_demo flex items-center justify-center py-12 bg-white/[0.02] border border-white/5 rounded-3xl relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
          <h3 id="split-stagger" className="font-display font-black text-4xl md:text-6xl text-white flex select-none">
            {"STAGGER...".split("").map((char, i) => (
              <span key={i} className="lag-char inline-block text-gradient mx-0.5">
                {char}
              </span>
            ))}
          </h3>
        </div>
      </div>

      {/* 7. Double Parallax Images */}
      <div className="container-x py-16 md:py-24 max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10">
        <div className="parallax-text md:order-2">
          <div className="content border-l-2 border-[var(--glow-purple)] pl-6">
            <span className="text-xs font-mono tracking-[0.2em] uppercase text-muted-foreground">Easy Parallax Effects</span>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white mt-2">Parallax Image Grids.</h3>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Pop your images in a container with overflow hidden, size them a little larger than the container and set data-speed to auto. GSAP handles the rest.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:gap-8 md:order-1 w-full">
          <div className="image_cont h-[220px] md:h-[320px] overflow-hidden rounded-3xl border border-white/5 relative" data-speed="0.85">
            <img data-speed="auto" src="/images/showcase/card-1.jpg" className="absolute bottom-0 w-full h-[140%] object-cover" alt="Parallax Grid Showcase 1" />
          </div>
          <div className="image_cont h-[220px] md:h-[320px] overflow-hidden rounded-3xl border border-white/5 relative mt-12 md:mt-20" data-speed="1.2">
            <img data-speed="auto" src="/images/showcase/card-2.jpg" className="absolute bottom-0 w-full h-[140%] object-cover" alt="Parallax Grid Showcase 2" />
          </div>
        </div>
      </div>

    </section>
  );
}

// --- 8. AGENCY VISION SLIDES STORY (SCRUBBED STICKY SLIDES) ---
export function AgencyVision() {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const slides = [
    {
      label: "Strategic Vision",
      title: "We Shape Digital Attention.",
      subtitle: "KMWA designs high-performance brand systems engineered to capture user awareness and scale reach.",
      img: "/images/showcase/ind-gaming.jpg"
    },
    {
      label: "Performance Allocation",
      title: "Optimized For Maximum ROAS.",
      subtitle: "Dynamic programmatic media buying algorithms routing capital directly into high-converting consumer hubs.",
      img: "/images/showcase/ind-fmcg.jpg"
    },
    {
      label: "Technology Architecture",
      title: "Built For Conversion Speed.",
      subtitle: "Unified web applications built with modern frontend frameworks to compound search authority and performance.",
      img: "/images/showcase/ind-hospitality.jpg"
    },
    {
      label: "Creative Vector",
      title: "Cinematic Creative Assembly.",
      subtitle: "Crafting bespoke digital billboard and social feed creative assets tailored for demographically audited targets.",
      img: "/images/showcase/ind-healthcare.jpg"
    },
    {
      label: "Future Integrations",
      title: "Exponential Channel Scale.",
      subtitle: "Partner with Kris Media Work to audit, optimize, and dominate your market. Scroll to continue.",
      img: "/images/showcase/ind-realestate.jpg"
    }
  ];

  const handleDotClick = (index: number) => {
    if (!containerRef.current) return;
    const containerTop = containerRef.current.offsetTop;
    const containerHeight = containerRef.current.offsetHeight;
    const targetY = containerTop + (index / (slides.length - 1)) * (containerHeight - window.innerHeight);
    const lenis = (window as any).lenis;
    if (lenis) {
      lenis.scrollTo(targetY, { duration: 1.2 });
    } else {
      window.scrollTo({ top: targetY, behavior: "smooth" });
    }
  };

  const handleExitDown = () => {
    const nextSection = document.getElementById("spiral");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (typeof window === "undefined" || !containerRef.current || !viewportRef.current) return;

    const viewport = viewportRef.current;
    const slideElements = viewport.querySelectorAll(".journey-slide");
    const outerWrappers = viewport.querySelectorAll(".journey-outer");
    const innerWrappers = viewport.querySelectorAll(".journey-inner");
    const images = viewport.querySelectorAll(".journey-bg");

    // Initialize all slides
    slides.forEach((_, idx) => {
      if (idx === 0) {
        gsap.set(slideElements[0], { autoAlpha: 1, zIndex: 1 });
        gsap.set(outerWrappers[0], { yPercent: 0 });
        gsap.set(innerWrappers[0], { yPercent: 0 });
        gsap.set(images[0], { yPercent: 0 });
      } else {
        gsap.set(slideElements[idx], { autoAlpha: 0, zIndex: 0 });
        gsap.set(outerWrappers[idx], { yPercent: 100 });
        gsap.set(innerWrappers[idx], { yPercent: -100 });
        gsap.set(images[idx], { yPercent: 15 });
      }
    });

    // Create scrubbed master timeline
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: "bottom bottom",
        scrub: 0.5,
        onUpdate: (self) => {
          const idx = Math.min(slides.length - 1, Math.max(0, Math.round(self.progress * (slides.length - 1))));
          setActiveSlide(idx);
        }
      }
    });

    // Sequence slide transitions seamlessly along scroll path
    const numTransitions = slides.length - 1;
    for (let i = 0; i < numTransitions; i++) {
      const current = slideElements[i];
      const next = slideElements[i + 1];
      const nextOuter = outerWrappers[i + 1];
      const nextInner = innerWrappers[i + 1];
      const nextImg = images[i + 1];
      const currentImg = images[i];
      const startTime = i;

      tl.to(currentImg, { yPercent: -15, ease: "none", duration: 1 }, startTime)
        .set(next, { autoAlpha: 1, zIndex: i + 2 }, startTime)
        .to(nextOuter, { yPercent: 0, ease: "none", duration: 1 }, startTime)
        .to(nextInner, { yPercent: 0, ease: "none", duration: 1 }, startTime)
        .to(nextImg, { yPercent: 0, ease: "none", duration: 1 }, startTime)
        .set(current, { autoAlpha: 0 }, startTime + 1);
    }

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      id="vision"
      className="relative w-full h-[500vh] bg-black select-none z-30"
    >
      {/* Sticky slides viewport */}
      <div 
        ref={viewportRef}
        className="sticky top-0 h-screen w-full overflow-hidden relative"
      >
        {/* Floating Header */}
        <header className="absolute top-20 sm:top-24 md:top-28 left-6 right-6 md:left-12 md:right-12 z-40 flex items-center justify-between pointer-events-auto">
          <div className="flex items-center gap-2 text-white">
            <Sliders className="h-4 w-4 text-[var(--glow-orange)] animate-spin-slow" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] font-semibold text-white/95">
              Agency Vision / Observer Slides
            </span>
          </div>
          <div className="flex items-center gap-6">
            <button 
              onClick={handleExitDown}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.15em] text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-all cursor-pointer backdrop-blur-md"
            >
              <span>Skip Showcase</span>
              <ArrowRight className="h-3 w-3 text-[var(--glow-orange)]" />
            </button>
          </div>
        </header>

        {slides.map((slide, idx) => (
          <div 
            key={idx}
            className="journey-slide absolute inset-0 w-full h-full overflow-hidden"
            style={{ 
              zIndex: idx === 0 ? 1 : 0, 
              visibility: idx === 0 ? "visible" : "hidden" 
            }}
          >
            <div className="journey-outer w-full h-full overflow-hidden">
              <div className="journey-inner w-full h-full overflow-hidden">
                <div 
                  className="journey-bg absolute inset-0 w-full h-[120%] bg-cover bg-center flex flex-col items-center justify-center"
                  style={{ 
                    backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.75) 40%, rgba(0,0,0,0.45) 100%), url(${slide.img})`,
                    top: "-10%"
                  }}
                >
                  <div className="container-x text-center max-w-4xl px-6 relative z-10">
                    <span className="text-xs sm:text-sm font-mono tracking-[0.3em] uppercase text-[var(--glow-orange)] mb-4 block font-semibold">
                      {slide.label}
                    </span>
                    <h2 className="font-display font-black text-3xl sm:text-5xl md:text-7xl uppercase leading-none tracking-tight text-white max-w-3xl mx-auto flex flex-wrap justify-center overflow-hidden py-3">
                      {slide.title}
                    </h2>
                    <p className="mt-6 text-xs md:text-sm text-white/70 max-w-md mx-auto leading-relaxed uppercase tracking-wider">
                      {slide.subtitle}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Floating Side Pagination */}
        <div className="absolute right-6 md:right-12 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-4 sm:gap-5 pointer-events-auto">
          {slides.map((slide, idx) => (
            <button
              key={idx}
              onClick={() => handleDotClick(idx)}
              className="group flex items-center gap-3.5 text-right justify-end focus:outline-none cursor-pointer"
            >
              <span className={`text-xs sm:text-sm font-mono tracking-wider transition-all duration-300 font-semibold ${
                activeSlide === idx 
                  ? "text-[var(--glow-orange)] font-bold translate-x-0 opacity-100 drop-shadow" 
                  : "text-white/70 opacity-0 group-hover:opacity-100 translate-x-2"
              }`}>
                {slide.label}
              </span>
              <div className={`h-3 w-3 sm:h-3.5 sm:w-3.5 rounded-full border transition-all duration-300 ${
                activeSlide === idx 
                  ? "bg-[var(--glow-orange)] border-[var(--glow-orange)] scale-125 shadow-[0_0_10px_var(--glow-orange)]" 
                  : "bg-transparent border-white/40 group-hover:border-white/80"
              }`} />
            </button>
          ))}
        </div>

        {/* Floating Bottom Navigator */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-40 text-center pointer-events-auto px-4">
          <button 
            onClick={handleExitDown}
            className="group inline-flex flex-col items-center gap-2 cursor-pointer"
          >
            <span className="font-mono text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-white/85 group-hover:text-white transition duration-300 drop-shadow-md">
              Scroll down to navigate through slides
            </span>
            <div className="h-9 w-9 rounded-full border border-white/20 flex items-center justify-center bg-black/60 backdrop-blur-md group-hover:border-white/50 group-hover:bg-black/80 transition duration-300 shadow-lg">
              <ArrowRight className="h-4 w-4 text-[var(--glow-orange)] rotate-90 animate-bounce" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}


